import Globe from '@apps-common/react-globe';
import {Paper, Stack, Typography} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import theme from '../theme.js';
import {formatDate} from '../util/date.js';
import {getCenterForGeometry} from '../util/map.js';

dayjs.extend(relativeTime);

const GLOBE_SIZE = 48;

/**
 * @param {string} dateString A formatted date string.
 * @returns {string} A relative time string.
 */
function formatDistanceToNow(dateString) {
  return dayjs(dateString).fromNow();
}

const COLUMNS = [
  {
    field: 'geometry',
    headerName: '',
    flex: 0.15,
    sortable: false,
    showMenu: false,
    align: 'center',
    renderCell: ({value}) => (
      <Stack sx={{height: '100%', justifyContent: 'center'}}>
        <Globe
          center={getCenterForGeometry(value)}
          markerColor="#00A0B4"
          landColor={theme.palette.grey[500]}
          waterColor="#fff"
          diameter={GLOBE_SIZE}
        />
      </Stack>
    ),
    resizable: false,
  },
  {
    field: 'title',
    headerName: 'Name',
    flex: 1,
    renderCell: ({row}) => {
      return (
        <Stack sx={{gap: 0.5, justifyContent: 'center', height: '100%'}}>
          <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>
            {row.title}
          </Typography>
          <Typography variant="body2" noWrap>
            {row.description}
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: 'startTime',
    headerName: 'Start',
    flex: 0.25,
    valueFormatter: value => formatDate(new Date(value)),
    resizable: false,
  },
  {
    field: 'endTime',
    headerName: 'End',
    flex: 0.25,
    valueFormatter: value => formatDate(new Date(value)),
    resizable: false,
  },
  {
    field: 'created',
    headerName: 'Created',
    flex: 0.25,
    valueFormatter: value => formatDistanceToNow(value),
    resizable: false,
  },
];

const createFilterFunction = filter => {
  const invert = filter.substring(0, 1) === '!';
  const fstr = (invert ? filter.substring(1) : filter).toLowerCase();

  if (!fstr) {
    return () => true;
  }

  return sub => {
    const match =
      sub.description?.toLowerCase().indexOf(fstr) >= 0 ||
      sub.title?.toLowerCase().indexOf(fstr) >= 0;
    return (invert && !match) || (!invert && match);
  };
};

const createFilter = filter => {
  if (!filter) {
    return () => true;
  }

  const filterFuncs = filter
    .replace(/ +/g, ' ')
    .split(' ')
    .map(word => createFilterFunction(word));
  const len = filterFuncs.length;

  return sub => {
    for (let i = 0; i < len; i++) {
      if (!filterFuncs[i](sub)) {
        return false;
      }
    }
    return true;
  };
};

function SubscriptionList({subscriptions, feeds, filter}) {
  const history = useHistory();
  const matchFunc = useMemo(() => createFilter(filter), [filter]);

  const subsWithFeeds = useMemo(() => {
    const feedsMap = {};
    for (let i = 0, ii = feeds.length; i < ii; i++) {
      const id = feeds[i].id;
      feedsMap[id] = i;
    }

    const subsWithFeeds = [];
    for (let i = 0, ii = subscriptions.length; i < ii; i++) {
      const subscription = subscriptions[i];
      if (matchFunc(subscription)) {
        subsWithFeeds.push({
          ...subscription,
          feed: feeds[feedsMap[subscription.feedID]],
        });
      }
    }

    return subsWithFeeds;
  }, [subscriptions, feeds, matchFunc]);

  const handleClickRow = subscription => {
    history.push(`/subscription/${subscription.id}/`);
  };

  return (
    <Paper sx={{display: 'flex', flex: 1, overflow: 'hidden', height: '100%'}}>
      <DataGrid
        disableColumnMenu
        columns={COLUMNS}
        rows={subsWithFeeds}
        rowHeight={68}
        onRowClick={handleClickRow}
        sx={{
          '& .pl-globe': {
            display: 'flex',
            justifyContent: 'center',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
            backgroundColor: theme => theme.vars.palette.primary.light,
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'rgb(242, 242, 242)',
          },
          border: 'none',
        }}
      />
    </Paper>
  );
}

SubscriptionList.propTypes = {
  subscriptions: PropTypes.array,
  feeds: PropTypes.array,
  filter: PropTypes.string,
};

export default SubscriptionList;
